import React, { forwardRef, useImperativeHandle } from 'react';
import type { ImageModel, PassportArrival } from '@/types';
import {
  useRepresentativeImage,
  type RepresentativeImageExportGuard,
} from '@/api/ProductImages/useRepresentativeImage';
import { GalleryImages } from '@/ui/components/ImagesLightBox';
import { useTranslate } from '@/translations';

export interface VehicleImagesHandle {
  prepareExport: () => Promise<RepresentativeImageExportGuard>;
}

export const VehicleImages = forwardRef<
  VehicleImagesHandle,
  {
    vehicleId?: number;
    images: ImageModel[];
    exteriorImageCount?: number;
    enabled?: boolean;
    placeholder?: string;
    arrival?: Pick<PassportArrival, 'vehicleArrivalId' | 'photos'> | null;
  }
>(function VehicleImages(
  {
    vehicleId,
    images,
    exteriorImageCount,
    enabled = true,
    placeholder = '/images/car.png',
    arrival,
  },
  ref,
) {
  const { t } = useTranslate();
  const { image, prepareExport } = useRepresentativeImage(vehicleId, enabled);
  useImperativeHandle(ref, () => ({ prepareExport }), [prepareExport]);
  const realImages = images.map((photo) => ({
    imageFileUrl: photo.url,
    thumbnailImageUrl: photo.resizedImageUrl,
  }));
  // Older Passport responses do not classify photos, so keep their existing lead image.
  const insertionIndex = Math.max(
    0,
    Math.min(exteriorImageCount ?? realImages.length, realImages.length),
  );
  const withRepresentative = image
    ? [
        ...realImages.slice(0, insertionIndex),
        {
          imageFileUrl: image.url,
          thumbnailImageUrl: image.url,
          contentType: 'image/jpeg',
          caption: t('imagery.generated'),
          downloadFileName: `representative-image-${image.descriptor.assetId}.jpg`,
        },
        ...realImages.slice(insertionIndex),
      ]
    : realImages;
  const arrivalPhotos = arrival?.photos ?? [];
  const presented = [
    ...withRepresentative,
    ...arrivalPhotos.map((photo, index) => ({
      imageFileUrl: photo.url,
      thumbnailImageUrl: photo.resizedImageUrl,
      caption: t('arrivalPhotoCaption', {
        number: index + 1,
        total: arrivalPhotos.length,
      }),
    })),
  ];
  return presented.length ? (
    <GalleryImages
      key={`${image?.descriptor.assetId ?? 'real-photos'}:${arrival?.vehicleArrivalId ?? 'no-arrival'}`}
      images={presented}
    />
  ) : (
    <div className="h-100 d-flex align-items-center justify-content-center">
      <img className="h-50" src={placeholder} alt="No vehicle images" />
    </div>
  );
});
